import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { MessageorderModel } from 'projects/core/src/lib/models/messageorder.model';
import { RecommendationModel } from 'projects/core/src/lib/models/recommendation.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-messageorder-settings',
  templateUrl: './messageorder-settings.component.html',
  styleUrls: ['./messageorder-settings.component.scss']
})
export class MessageorderSettingsComponent implements OnInit, OnChanges {

  @Input() form: UntypedFormGroup = null;
  @Input() messageorder: MessageorderModel = null;
  @Input() options: any = null;
  @Input() inEdit: boolean = true;

  public optionsTemplates: Array<SelectItem> = [];
  public optionsBusinessunits: Array<SelectItemGroup> = [];
  public optionsRecommendations: Array<SelectItem> = [];
  public formInit = false;

  constructor(
    private fb: UntypedFormBuilder,
    private helper: HelperService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.buildOptions();
    this.buildForm();
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formInit === true && (changes.messageorder || changes.inEdit)) {
      this.initForm();
    }
  }

  public getPayload(): any {
    const payload = {
      conditions: {
        templates: this.form.get('conditions.templates').value || [],
        businessunits: this.form.get('conditions.businessunits').value || [],
        recommendations: this.form.get('conditions.recommendations').value || [],
        afterreport: this.form.get('conditions.afterreport').value || false,
      },
      options: {
        reportfile: this.form.get('options.reportfile').value,
      },
      recipients: {},
      emails: []
    };
    this.options?.roles?.forEach((role: string) => {
      payload.recipients[role] = this.form.get('recipients.' + role).value;
    });
    this.form.get('emails').value?.forEach((value: any) => {
      payload.emails.push(value.email);
    });
    return payload;
  }

  // input handling

  private clearEmailEntries(): void {
    const entries = this.form.get('emails') as UntypedFormArray;
    while (entries.length > 0) {
      entries.removeAt(0);
    }
  }

  public addEmailEntry(data: string = ''): void {
    const entries = this.form.get('emails') as UntypedFormArray;
    entries.push(this.createEmailEntry(data));
  }

  public deleteEmailEntry(index: number): void {
    const entries = this.form.get('emails') as UntypedFormArray;
    if (entries.length > 0) {
      entries.removeAt(index);
    }
  }

  private createEmailEntry(data: string = ''): UntypedFormGroup {
    return this.fb.group({
      email: new UntypedFormControl(data, [Validators.required, CustomValidators.email()])
    });
  }

  private buildOptions(): void {
    if (this.options !== null) {
      this.optionsTemplates = this.options.templates.map((template: TemplateModel) => {
        return {
          value: template.id,
          label: template.name,
        };
      });
      this.optionsBusinessunits = this.helper.getGroupOptions(this.options.businessunits, 'name', 'businessunits');
      this.optionsRecommendations = this.options.recommendations.map((recommendation: RecommendationModel) => {
        return {
          value: recommendation.id,
          label: recommendation.name[this.transloco.getActiveLang().toUpperCase()],
        };
      });
    }
  }

  private buildForm(): void {
    if (this.form) {
      this.form.addControl('conditions', this.fb.group({
        templates: new UntypedFormControl(null),
        businessunits: new UntypedFormControl(null),
        recommendations: new UntypedFormControl(null),
        afterreport: new FormControl<boolean>(false),
      }));
      this.form.addControl('options', this.fb.group({
        reportfile: new UntypedFormControl(false),
      }));
      const recipientControls = {};
      this.options?.roles?.forEach((role: string) => {
        recipientControls[role] = new UntypedFormControl(false);
      });
      this.form.addControl('recipients', this.fb.group(recipientControls));
      this.form.addControl('emails', this.fb.array([]));
      this.form.setValidators([
        FormValidators.recipients(this.options?.roles)
      ]);
      this.formInit = true;
    }
  }

  private initForm(): void {
    this.form.get('conditions').patchValue({
      templates: (Array.isArray(this.messageorder?.conditions?.templates)) ? this.messageorder?.conditions?.templates : [],
      businessunits: (Array.isArray(this.messageorder?.conditions?.businessunits)) ? this.messageorder?.conditions?.businessunits : [],
      recommendations: (Array.isArray(this.messageorder?.conditions?.recommendations)) ? this.messageorder?.conditions?.recommendations : [],
      afterreport: this.messageorder?.conditions?.afterreport === true,
    });
    this.form.get('options').patchValue({
      reportfile: this.messageorder?.options?.reportfile || false
    });
    this.options?.roles?.forEach((role: string) => {
      this.form.get('recipients.' + role).setValue(this.messageorder?.recipients[role] || false);
    })
    this.clearEmailEntries();
    this.messageorder?.emails?.forEach((email: string) => {
      this.addEmailEntry(email);
    });
  }

  // getter

  public getTemplateNames(): string {
    const templates = [];
    this.optionsTemplates?.forEach((template: SelectItem) => {
      if (this.messageorder?.conditions?.templates?.indexOf(template.value) !== -1) {
        templates.push(template.label);
      }
    });
    return templates.join(', ');
  }

  public getBusinessunitNames(): string {
    const businessunits = [];
    if (this.optionsBusinessunits && this.messageorder?.conditions?.businessunits) {
      this.optionsBusinessunits.forEach((parent: SelectItemGroup) => {
        parent.items.forEach((businessunit: SelectItem) => {
          if (this.messageorder?.conditions?.businessunits?.indexOf(businessunit.value) !== -1) {
            businessunits.push(businessunit.label);
          }
        });
      });
    }
    return businessunits.join(', ');
  }

  public getRecommendationNames(): string {
    const recommendations = [];
    this.optionsRecommendations?.forEach((recommendation: SelectItem) => {
      if (this.messageorder?.conditions?.recommendations?.indexOf(recommendation.value) !== -1) {
        recommendations.push(recommendation.label);
      }
    });
    return recommendations.join(', ');
  }

  public getOption(option: string): boolean {
    return this.messageorder?.options[option] || false;
  }

  public getRecipient(role: string): boolean {
    return this.messageorder?.recipients[role] || false;
  }

  public getEmail(index: number): string {
    return this.messageorder?.emails[index] || '';
  }

  public hasConditionsError(): boolean {
    return this.form.get('conditions').dirty &&
      this.form.invalid &&
      this.form.errors?.conditions === true;
  }

  public hasRecipientsError(): boolean {
    return (this.form.get('recipients').dirty || this.form.get('emails').dirty) &&
      this.form.invalid &&
      this.form.errors?.recipients === true;
  }

}

class FormValidators {

  static conditions(): ValidatorFn { // @deprecated: nicht gewünscht
    return (group: UntypedFormGroup): {[key: string]: any} | null => {
      if (
        group.get('conditions.templates').value === null &&
        group.get('conditions.businessunits').value === null &&
        group.get('conditions.recommendations').value === null
      ) {
        return {
          conditions: true
        };
      }
      return null;
    };
  }

  static recipients(roles: Array<string>): ValidatorFn {
    return (group: UntypedFormGroup): {[key: string]: any} | null => {
      let recipient = false;
      roles?.forEach((role: string) => {
        if (group.get('recipients.' + role).value === true) {
          recipient = true;
        }
      })
      if (
        recipient === false &&
        (group.get('emails') as UntypedFormArray).controls.length === 0
      ) {
        return {
          recipients: true
        };
      }
      return null;
    };
  }

}
